import * as yup from "yup";

import { useForm, useFormState } from "react-hook-form";

import { OPTION } from "../misc/constants";
import Validations from "@helpers/validations";
import { object } from "yup";
import useAxios from "@hooks/use-axios";
import { useEffect } from "react";
import { useParams } from "react-router-dom";
import { yupResolver } from "@hookform/resolvers/yup";

export const defaultValues = {
    _id              : "",
    name             : "",
    description      : "",
    completedClients : 0,
    inProgressClients: 0,
    status           : "",
    conditions       : [
        {
            condition: "",
            name     : "",
            operator : "",
            value    : 0
        }
    ],
    flow_steps: [
        {
            name       : "new_task",
            description: "",
            due_date   : new Date(),
            props_name : "",
            survey_id  : ""
        }
    ]
};

const useUpsert = props => {
    const { platformId } = useParams();  
    const {
        modalConfig: { row },
        handleModal,
        getPlaybooks
    } = props;
    const { loading, axiosInstance } = useAxios({
        base: "panel"
    });

    const {
        requiredValidation,
        numberValidation,
        arrayMinMaxLengthValidation,
        emptyObjValidation
    } = Validations();

    const methods = useForm({
        defaultValues,
        resolver: yupResolver(
            object().shape({
                _id              : yup.mixed().nullable(),
                inProgressClients: yup.mixed().nullable(),
                completedClients : yup.mixed().nullable(),
                status           : yup.mixed().nullable(),
                name             : requiredValidation({ name: "Name" }),
                description      : requiredValidation({ name: "Description" }),

                conditions: arrayMinMaxLengthValidation({
                    name: "Condition",
                    min : 1
                }).of(
                    object().shape({
                        condition: requiredValidation({ name: "Condition" }),
                        name     : requiredValidation({ name: "Condition Name" }),
                        operator : requiredValidation({ name: "Operator" }),
                        value    : yup.mixed().when("name", {
                            is       : name => name === "billing_frequency",
                            then     : () => requiredValidation({ name: "Value" }),
                            otherwise: () => numberValidation({ name: "Value", min: 0 })
                        })
                    })
                ),

                flow_steps: arrayMinMaxLengthValidation({
                    name: "Step",
                    min : 1
                }).of(
                    object().shape({
                        name      : requiredValidation({ name: "Type" }),
                        props_name: yup.mixed().when("name", {
                            is       : name => name === "new_task",
                            then     : () => requiredValidation({ name: "Name" }),
                            otherwise: () => yup.mixed().nullable()
                        }),
                        description: yup.mixed().when("name", {
                            is       : name => name === "new_task",
                            then     : () => requiredValidation({ name: "Description" }),
                            otherwise: () => yup.mixed().nullable()
                        }),
                        due_date: yup.mixed().when("name", {
                            is       : name => name === "new_task",
                            then     : () => requiredValidation({ name: "Due Date" }),
                            otherwise: () => yup.mixed().nullable()
                        }),
                        survey_id: yup.mixed().when("name", {
                            is       : name => name === "new_task",
                            then     : () => yup.mixed().nullable(),
                            otherwise: () => emptyObjValidation({ name: "Survey ID" })
                        })
                    })
                )
            })
        )
    });

    const { control, handleSubmit, reset } = methods;
    const { isDirty } = useFormState({ control });

    const getPlaybookDetails = async () => {
        const { data } = await axiosInstance.get(`/playbooks/${platformId}/${row._id}`);

        const transformedSteps = data.flow_steps.map(step => {
            if (step.name === "send_survey") {
                return {
                    name     : step.name,
                    survey_id: {
                        value: step.props?.survey_id,
                        label: step.props?.name
                    },
                    due_date   : null,
                    description: "",
                    props_name : ""
                };
            }

            return {
                name       : step.name,
                props_name : step.props?.name,
                description: step.props?.description,
                due_date   : step.props?.due_date ? new Date(step.props.due_date) : new Date(),
                survey_id  : ""
            };
        });

        reset({
            _id              : data._id,
            name             : data.name,
            description      : data.description,
            completedClients : data.completedClients,
            inProgressClients: data.inProgressClients,
            status           : data.status,
            conditions:
                data.start_trigger?.conditions || defaultValues.conditions,
            flow_steps: transformedSteps
        });
    };

    useEffect(() => {
        let isActive = true;
        if (row?._id && isActive) {
            getPlaybookDetails();
        } else {
            reset(defaultValues);
        }

        return () => {
            isActive = false;
        };
    }, [row]);

    const onSubmit = () => {
        handleSubmit(async data => {
            const payload = {
                name         : data.name,
                description  : data.description,
                status       : data.status || OPTION.ACTIVE,
                start_trigger: {
                    name      : OPTION.CUSTOM,
                    conditions: data.conditions
                },
                flow_steps: data.flow_steps.map((step, index) => {
                    if (step.name === "send_survey") {
                        return {
                            name   : step.name,
                            step_no: index,
                            props  : {
                                survey_id: step.survey_id.value
                            }
                        };
                    }
                    return {
                        name   : step.name,
                        step_no: index,
                        props  : {
                            description: step.description,
                            due_date   : step.due_date,
                            name       : step.props_name
                        }
                    };
                })
            };

            if (row._id) {
                await axiosInstance.patch(`/playbooks/${platformId}/${row._id}`, payload);
            } else {
                await axiosInstance.post(`/playbooks/${platformId}`, payload);
            }
            handleModal();
            getPlaybooks();
        })();
    };

    return {
        onSubmit,
        methods,
        updateLoading: loading,
        isDirty,
        loading
    };
};
export default useUpsert;
